import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STEPS = [
  { label: 'diagram.process.step1.label', description: 'diagram.process.step1.desc', color: '#22D3C5', delay: 0 },
  { label: 'diagram.process.step2.label', description: 'diagram.process.step2.desc', color: '#7057FF', delay: 0.5 },
  { label: 'diagram.process.step3.label', description: 'diagram.process.step3.desc', color: '#22D3C5', delay: 1.0 },
  { label: 'diagram.process.step4.label', description: 'diagram.process.step4.desc', color: '#7057FF', delay: 1.5 },
  { label: 'diagram.process.step5.label', description: 'diagram.process.step5.desc', color: '#22D3C5', delay: 2.0 },
];

export default function ProcessDiagram({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Desktop horizontal layout */}
      <div className="hidden md:block relative">
        {/* Connecting line */}
        <div className="absolute top-[28px] left-[10%] right-[10%] h-0.5 bg-background-200">
          <div
            className={`h-full bg-gradient-to-r from-secondary-400 via-accent-400 to-secondary-400 transition-all duration-[2000ms] ease-out ${
              active ? 'w-full' : 'w-0'
            }`}
          />
        </div>

        {/* Animated particles along the line */}
        {active && (
          <div className="absolute top-[24px] left-[10%] right-[10%] h-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="absolute top-0 w-2 h-2 rounded-full bg-accent-400 animate-[flowParticle_3s_linear_infinite]"
                style={{ animationDelay: `${i * 0.75}s` }}
              />
            ))}
          </div>
        )}

        <div className="grid grid-cols-5 gap-4">
          {STEPS.map((step, i) => (
            <div
              key={step.label}
              className={`flex flex-col items-center text-center transition-all duration-700 ${
                active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${step.delay}s` }}
            >
              {/* Node */}
              <div
                className={`relative z-10 w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                  active ? 'scale-100' : 'scale-75'
                }`}
                style={{
                  borderColor: step.color,
                  backgroundColor: active ? `${step.color}15` : 'transparent',
                  boxShadow: active ? `0 0 20px ${step.color}30` : 'none',
                  transitionDelay: `${step.delay + 0.2}s`,
                }}
              >
                <span className="text-lg font-heading font-bold" style={{ color: step.color }}>
                  {i + 1}
                </span>
                {/* Pulse ring */}
                {active && (
                  <div
                    className="absolute inset-0 rounded-full border-2 animate-[morphPulse_2s_ease-in-out_infinite]"
                    style={{ borderColor: step.color, animationDelay: `${step.delay}s` }}
                  />
                )}
              </div>

              {/* Label */}
              <div className="mt-4">
                <p
                  className="text-xs font-mono font-semibold uppercase tracking-widest mb-1"
                  style={{ color: step.color }}
                >
                  {t(step.label)}
                </p>
                <p className="text-[11px] text-foreground-600 leading-snug px-1">
                  {t(step.description)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile vertical layout */}
      <div className="md:hidden relative space-y-6">
        {/* Vertical line */}
        <div className="absolute top-4 bottom-4 left-7 w-0.5 bg-background-200">
          <div
            className={`w-full bg-gradient-to-b from-secondary-400 via-accent-400 to-secondary-400 transition-all duration-[2000ms] ease-out ${
              active ? 'h-full' : 'h-0'
            }`}
          />
        </div>

        {STEPS.map((step, i) => (
          <div
            key={step.label}
            className={`flex items-start gap-4 transition-all duration-700 ${
              active ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
            }`}
            style={{ transitionDelay: `${step.delay}s` }}
          >
            {/* Node */}
            <div
              className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center border-2 shrink-0"
              style={{
                borderColor: step.color,
                backgroundColor: active ? `${step.color}15` : 'transparent',
                boxShadow: active ? `0 0 15px ${step.color}25` : 'none',
              }}
            >
              <span className="text-lg font-heading font-bold" style={{ color: step.color }}>
                {i + 1}
              </span>
            </div>

            {/* Content */}
            <div className="pt-2">
              <p
                className="text-xs font-mono font-semibold uppercase tracking-widest mb-1"
                style={{ color: step.color }}
              >
                {t(step.label)}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {t(step.description)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes flowParticle {
          0% { left: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}